import { useQuery, useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { BASE_URL, listAllApprovals, updateApprovalItem, updateApproval, listAllApprovalItems } from '@/services/DataService';
import { ANALYTICS_EVENTS, agentSlugFromPath, useTrack } from '@/lib/analytics';

interface ApprovalQueryParams {
  page_size?: number;
  sort_by?: string;
  sort_order?: string;
  workflow_instance_id?: string;
  workflow_execution_id?: string;
}

/**
 * Hook to fetch approval items for a specific approval
 * @param approvalId The ID of the approval to fetch items for
 * @param options Additional react-query options
 * @returns Query result with approval items data
 */
export const useApprovalItems = (approvalId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['approvalItems', approvalId],
    queryFn: () => listAllApprovalItems(BASE_URL, approvalId),
    enabled: options?.enabled !== false && !!approvalId,
    staleTime: 1000 * 60, // 1 min
  });
};

/**
 * Hook to fetch approvals with optional query parameters
 * @param params Optional query parameters for filtering approvals
 * @param options Additional react-query options
 * @returns Query result with approvals data
 */
export const useApprovals = (params?: ApprovalQueryParams, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['approvals', params?.workflow_instance_id, params?.workflow_execution_id],
    queryFn: () => listAllApprovals(BASE_URL, params),
    enabled: options?.enabled !== false,
    staleTime: 0,
    gcTime: 0
  });
};

// types for the approval item mutation parameters
export type UpdateApprovalItemParams = {
  approvalId: string;
  approvalItemId: string;
  status: string;
  reviewedData?: Record<string, unknown>;
  feedbackText?: string;
};

/**
 * Hook to update an approval item's status
 * @param options Optional mutation options to customize behavior
 * @returns Mutation for updating approval item
 */
export const useUpdateApprovalItem = (
  options?: Omit<UseMutationOptions<unknown, Error, UpdateApprovalItemParams>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ 
      approvalId, 
      approvalItemId, 
      status, 
      reviewedData, 
      feedbackText 
    }: UpdateApprovalItemParams) => {
      return updateApprovalItem(BASE_URL, approvalId, approvalItemId, status, reviewedData, feedbackText);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
      options?.onSuccess?.(data, variables, context);
    },
  });
};

// types for the approval mutation parameters
export type UpdateApprovalParams = {
  approvalId: string;
  status: string;
};

/**
 * Hook to update an approval's status
 * @param options Optional mutation options to customize behavior
 * @returns Mutation for updating approval status
 */
export const useUpdateApproval = (
  options?: Omit<UseMutationOptions<unknown, Error, UpdateApprovalParams>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();
  const trackEvent = useTrack();

  return useMutation({
    ...options,
    mutationFn: ({ 
      approvalId, 
      status 
    }: UpdateApprovalParams) => {
      return updateApproval(BASE_URL, approvalId, status);
    },
    onSuccess: (data, variables, context) => {
      // Only the approval-level decision is captured. Per-item updates
      // (useUpdateApprovalItem) fire once per row — a single keyword review
      // would bury every other event in the dataset.
      trackEvent(ANALYTICS_EVENTS.AGENT_APPROVAL_ACTIONED, {
        agent_slug: agentSlugFromPath(window.location.pathname),
        action: variables.status?.toUpperCase() === 'APPROVED' ? 'approved' : 'rejected',
      });
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
      options?.onSuccess?.(data, variables, context);
    },
  });
};
